import React, { useEffect, useRef, useState } from "react";
import { Cloud, CloudOff, RefreshCw } from "lucide-react";
import { getOfflineQueue } from "@/lib/offline";
import { syncOfflineQueue, warmOfflineCache } from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function OfflineStatus() {
  const navigate = useNavigate();
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pending, setPending] = useState(() => getOfflineQueue().length);
  const [syncing, setSyncing] = useState(false);
  const syncingRef = useRef(false);

  const refresh = () => setPending(getOfflineQueue().length);

  const doSync = async () => {
    if (syncingRef.current || !navigator.onLine || getOfflineQueue().length === 0) return;
    syncingRef.current = true; setSyncing(true);
    try { await syncOfflineQueue(); } finally { refresh(); setSyncing(false); syncingRef.current = false; }
  };

  useEffect(() => {
    const onOnline = () => { setOnline(true); doSync(); warmOfflineCache(true); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queue-changed", refresh);
    // Flush anything left over from a previous session on load
    doSync();
    // Warm the offline cache so key screens work offline even if not visited yet
    warmOfflineCache(true);
    // Periodic retry: navigator.onLine can be wrong (LAN up but internet down),
    // so keep trying while there are queued operations.
    const timer = setInterval(() => { if (getOfflineQueue().length > 0) doSync(); }, 30000);
    // Refresh the offline cache every 5 min while online
    const warmTimer = setInterval(() => warmOfflineCache(), 300000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queue-changed", refresh);
      clearInterval(timer);
      clearInterval(warmTimer);
    };
  }, []); // eslint-disable-line

  const label = online
    ? (pending ? `مزامنة ${pending}` : "متصل")
    : (pending ? `محفوظ ${pending}` : "غير متصل");
  const title = online
    ? (pending ? `متصل · ${pending} عملية بانتظار المزامنة — اضغط لعرض التفاصيل` : "متصل")
    : `غير متصل · ${pending} عملية محفوظة محلياً — اضغط لعرض التفاصيل`;

  return (
    <button
      type="button"
      onClick={() => { doSync(); navigate("/sync-queue"); }}
      title={title}
      data-testid="offline-status"
      className={`flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-colors ${online ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"} ${pending ? "ring-1 ring-current/30" : ""}`}
    >
      {online ? (syncing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Cloud className="w-3 h-3" />) : <CloudOff className="w-3 h-3" />}
      <span>{label}</span>
    </button>
  );
}
